import { ActionsBar, Section } from '@ydistri/ds';
import React, { useEffect, useMemo } from 'react';

import { getCategoryDetailType, getProductDetailType } from '../../detailLib';
import { CategoryDetailType, DetailType, ProductDetailType, UrlParams } from '../../detailTypes';
import useCategoryId from '../../../../hooks/useCategoryId';
import EntitiesTable from './components/EntitiesTable';
import RegionSelectorController from './components/RegionSelector/RegionSelectorController';
import StoreSelectorController from './components/StoreSelector/StoreSelectorController';
import DetailSkuAttributesController from './components/DetailSKUAttributes/DetailSkuAttributesController';
import useDetailScreenType from '../../hooks/useDetailScreenType';
import ClearSkuNotFoundAction from './components/ClearSkuNotFoundAction';
import { useDetail } from '../../hooks/useDetail';
import { useGetSingleStoreQuery } from '../../apiDetail';
import { useSearchParams } from 'react-router';
import LoadingDataOverlay from '../../../../components/global/LoadingDataOverlay/LoadingDataOverlay';
import { NO_VALUE } from '../../../../lib/utils/utilsTypes';
import { styled } from 'styled-components';
import { useAppDispatch } from '../../../../store';
import { ROOT_CATEGORY_TITLE } from '../../../../components/global/_constants';
import { isRootCategory } from '../../../../lib/categories/categoriesLib';

//Makes spaces for the Loading data spinner so it not overlaps an empty section to empty space
const LoadingDataWrapper = styled.div`
  min-height: 60px;
`;

const EntityTablesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2rem;
`;

const DetailContentHeaderSection: React.FC = () => {
  const selectedCategoryId = useCategoryId();
  const dispatch = useAppDispatch();
  const { storeId, skuId, productId, regionId, skuSearchId, isLoadingSalesData } = useDetail(
    'storeId',
    'skuId',
    'productId',
    'regionId',
    'skuSearchId',
    'isLoadingSalesData',
  );
  const { data: storeEntity } = useGetSingleStoreQuery(storeId);
  const [, setSearchParams] = useSearchParams();

  /**
   * If the store is not in the region, clear the storeId
   * Happens when store is selected and different region is selected
   */
  useEffect(() => {
    if (regionId !== NO_VALUE && storeId !== NO_VALUE && storeEntity) {
      if (storeEntity.region?.id !== regionId) {
        setSearchParams(prevParams => {
          prevParams.delete(UrlParams.STORE);
          return prevParams;
        });
      }
    }
  }, [storeId, regionId, storeEntity, dispatch, setSearchParams]);

  const detailType = useDetailScreenType(productId, storeId, skuId);

  const categoryDetailType = getCategoryDetailType(productId, regionId, storeId);
  const productDetailType = getProductDetailType(productId, regionId, storeId);

  const detailTypeHeader = useMemo(() => {
    let result = '';
    switch (detailType) {
      case DetailType.CATEGORY: {
        let prefix = '';
        switch (categoryDetailType) {
          case CategoryDetailType.DEFAULT:
            prefix = 'Detail';
            break;
          case CategoryDetailType.STORE:
            prefix = 'Store detail';
            break;
          case CategoryDetailType.REGION:
            prefix = 'Region detail';
            break;
          case undefined:
            prefix = 'Detail';
        }
        result = `${prefix} ${isRootCategory(selectedCategoryId) ? `(${ROOT_CATEGORY_TITLE})` : `(Category)`}`;
        break;
      }
      case DetailType.SKU: {
        let suffix = 'loading...';
        if (skuId !== -1) {
          suffix = skuId.toString();
        }
        result = `SKU detail - ${suffix}`;
        break;
      }
      case DetailType.PRODUCT: {
        switch (productDetailType) {
          case ProductDetailType.DEFAULT:
            result = 'Product detail (company)';
            break;
          case ProductDetailType.REGION:
            result = 'Product detail (region)';
            break;
          case undefined:
            result = 'Product detail';
            break;
        }
        break;
      }
      case DetailType.SKU_NOT_FOUND: {
        const message = 'SKU detail - non-existing SKU';
        if (skuSearchId !== undefined) {
          result = `${message}: ${skuSearchId}`;
          break;
        } else {
          result = message;
          break;
        }
      }
    }

    return result;
  }, [categoryDetailType, detailType, productDetailType, selectedCategoryId, skuId, skuSearchId]);

  const headerActions = useMemo(() => {
    const showRegionSelector = ![
      DetailType.PRODUCT,
      DetailType.SKU,
      DetailType.SKU_NOT_FOUND,
    ].includes(detailType);
    const showStoreSelector = ![
      DetailType.PRODUCT,
      DetailType.SKU,
      DetailType.SKU_NOT_FOUND,
    ].includes(detailType);

    const showResetSkuNotFound = [DetailType.SKU_NOT_FOUND].includes(detailType);

    return (
      <ActionsBar useDivider={true}>
        {showRegionSelector && <RegionSelectorController />}
        {showStoreSelector && <StoreSelectorController />}
        {showResetSkuNotFound && <ClearSkuNotFoundAction />}
      </ActionsBar>
    );
  }, [detailType]);

  return (
    <Section header={detailTypeHeader} $ratio={0} headerActions={headerActions}>
      {isLoadingSalesData ? (
        <LoadingDataWrapper>
          <LoadingDataOverlay active={true} />
        </LoadingDataWrapper>
      ) : (
        <EntityTablesWrapper>
          <EntitiesTable />
          <DetailSkuAttributesController />
        </EntityTablesWrapper>
      )}
    </Section>
  );
};

export default DetailContentHeaderSection;
